import {
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  forwardRef,
} from 'react';
import { ArrowRight } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { Button } from '.';
import { cn } from '../utils';

export interface PureInputProps {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  InputProps?: InputHTMLAttributes<HTMLInputElement>;
  withSubmitButton?: boolean;
  fullWidth?: boolean;
  submitButtonDisabled?: boolean;
  variant?: 'base' | 'empty';
  disableAutocomplete?: boolean;
  endAdoramentProps?: React.HTMLAttributes<HTMLDivElement>;
  hideNumberSpinButtons?: boolean;
  omitCharacters?: string[];
}
export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    PureInputProps {}

export const containerVariants = cva(
  'flex w-full items-center text-sm px-3 py-2 transition-colors duration-200 h-10 ',
  {
    variants: {
      variant: {
        base: cn(
          'rounded-sm border-[1.5px] border-border-soft borders text-text-muted hover:border-primary',
          'focus-within:border-primary focus-within:outline-border-primary'
        ),
        empty: '',
      },
    },
  }
);

export const inputVariants = cva(
  'w-full bg-inherit disabled:cursor-not-allowed focus-visible:outline-hidden text-primary transition-colors duration-200',
  {
    variants: {
      variant: {
        base: 'placeholder:text-muted-foreground disabled:opacity-50',
        empty: '',
      },
    },
  }
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      startAdornment,
      endAdornment,
      InputProps,
      withSubmitButton,
      fullWidth,
      submitButtonDisabled,
      variant = 'base',
      disableAutocomplete,
      endAdoramentProps,
      hideNumberSpinButtons,
      omitCharacters,
      ...props
    },
    ref
  ) => (
    <div
      className={cn({
        'flex flex-row items-center gap-2': withSubmitButton,
        'w-full': fullWidth,
      })}
    >
      <div className={cn(containerVariants({ variant }), className)}>
        {startAdornment && (
          <div className="text-primary mr-1 cursor-pointer">
            {startAdornment}
          </div>
        )}

        <input
          {...props}
          {...InputProps}
          className={cn(
            inputVariants({ variant }),
            {
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none':
                props.type === 'number' && hideNumberSpinButtons,
            },
            InputProps?.className
          )}
          ref={ref}
          {...(disableAutocomplete && {
            autoComplete: 'off',
          })}
          {...(omitCharacters && {
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
              if (omitCharacters.includes(e.key)) e.preventDefault();
              props.onKeyDown?.(e);
            },
          })}
        />

        {endAdornment && (
          <div
            {...endAdoramentProps}
            className={cn(
              'text-primary mx-1 cursor-pointer',
              endAdoramentProps?.className
            )}
          >
            {endAdornment}
          </div>
        )}
      </div>
      {withSubmitButton && (
        <Button disabled={props.disabled || submitButtonDisabled} size="icon">
          <ArrowRight />
        </Button>
      )}
    </div>
  )
);
Input.displayName = 'Input';

export { Input };
